import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  Collapse,
  Dropdown,
  Form,
  Spinner,
  Stack,
  Table,
} from "react-bootstrap"
import InfiniteScroll from "react-infinite-scroll-component"
import { toast } from "react-toastify"
import recordNotFound from "../../assets/images/record_not_found.png"
import PageTitleButton from "../../components/PageTitleButton"
import SearchBox from "../../components/SearchBox"
import SweetAlerts from "../../components/SweetAlerts"
import TooltipComponent from "../../components/Tooltip"
import { admin } from "../../config/apiEndpoints"
import axiosProvider from "../../lib/AxiosProvider"
import {
  getStatusClassName,
  useHandleUnauthorizedAccess,
  HasModulePermission,
} from "../../lib/GlobalMethods"
import { modulePermission } from "../../lib/GlobalVariables"
import useDebounce from "../../lib/hooks/useDebounce"
import ChargerListSkeleton from "../../skeleton/chargerManagement"
import AddEditCharger from "./AddEditCharger"
import { io } from "socket.io-client"

const ChargerManagement = () => {
  const canEdit = HasModulePermission(modulePermission.charger, "edit")
  const canDelete = HasModulePermission(modulePermission.charger, "delete")
  const canAdd = HasModulePermission(modulePermission.charger, "add")

  const [show, setShow] = useState(false)
  const [data, setData] = useState([])
  const [isUpdate, setIsUpdate] = useState(false)
  const [isDelete, setIsDelete] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [loading, setLoading] = useState(true)
  const [wsLoadingMap, setWsLoadingMap] = useState({})
  const [chargersWithStopPermission, setChargersWithStopPermission] = useState(
    {}
  )
  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [open, setOpen] = useState(false)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const [recordsCount, setRecordsCount] = useState({
    total_records: false,
    active_records: false,
    inactive_records: false,
  })
  const [filter, setFilter] = useState(false)
  const { handleAccess } = useHandleUnauthorizedAccess()

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const handleShow = () => setShow(true)

  const handleShowAlert = () => setShowAlert(true)

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setIsDelete(false)
    setShowAlert(false)
  }

  const setConnectorLoading = (chargerId, position, isLoading) => {
    setWsLoadingMap((prev) => ({
      ...prev,
      [`${chargerId}_${position}`]: isLoading,
    }))
  }

  const handleStatusChange = async (id, currentStatus) => {
    try {
      const newStatus =
        currentStatus === "Available" ? "Unavailable" : "Available"
      const response = await axiosProvider({
        method: "PATCH",
        endpoint: admin.updateCharger + id,
        data: { status: newStatus },
      })
      if (response?.data?.code === 200) {
        toast.success(response.data.message)

        if (filter !== false) {
          setData((data) =>
            data
              .map((charger) =>
                charger.id === id ? { ...charger, status: newStatus } : charger
              )
              .filter((charger) => charger.id !== id)
          )
        } else {
          setData((data) =>
            data.map((charger) =>
              charger.id === id ? { ...charger, status: newStatus } : charger
            )
          )
        }

        setRecordsCount((prevCounts) => {
          let newActiveCount = prevCounts.active_records
          let newInactiveCount = prevCounts.inactive_records

          if (newStatus === "Available") {
            newActiveCount += 1
            newInactiveCount -= 1
          } else if (newStatus === "Unavailable" && newActiveCount > 0) {
            newActiveCount -= 1
            newInactiveCount += 1
          }
          return {
            ...prevCounts,
            active_records: newActiveCount,
            inactive_records: newInactiveCount,
          }
        })
      } else {
        throw new Error(response.data.message)
      }
    } catch (error) {
      toast.error(error.message)
    }
  }

  const fetchData = async (searchQuery) => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.chargerList}?page=${
          pagination.current_page
        }&search_term=${
          searchQuery ? searchQuery : ""
        }&limit=15&status=${filter}`,
      })

      handleAccess({ code: response?.data?.code })

      const fetchedData = response?.data?.data || []
      setHasMore(true)

      response?.data?.code === 200 && setPagination(response?.data?.pagination)
      response?.data?.code === 200 &&
        setRecordsCount(response?.data?.record_counts)

      if (
        !fetchedData.length ||
        response.data.pagination.current_page ===
          response.data.pagination.total_pages
      ) {
        setHasMore(false)
      }

      setData((prevData) =>
        pagination.current_page === 1
          ? fetchedData
          : [...prevData, ...fetchedData]
      )

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  const transactionControl = async (id, position, stop) => {
    const key = `${id}_${position}`
    setConnectorLoading(id, position, true)
    try {
      const data = {
        charger_id: id,
        position,
      }
      const response = await axiosProvider({
        method: "POST",
        url: "socket",
        endpoint: stop ? admin.stopTransaction : admin.startTransaction,
        data,
      })
      if (response?.data?.code === 200) {
        toast.success(response.data.message)
      } else {
        throw new Error(response.data.message)
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    } finally {
      setConnectorLoading(id, position, false)
    }
  }

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      transports: ["websocket"],
    })

    socket.on("connect", () => {
      socket.on("status-update", (message) => {
        const isArray = Array.isArray(message)
        const keysToReset = []

        let newData

        setData((prevData) => {
          newData = prevData.map((charger) => {
            if (isArray) {
              const updatedConnectors = charger.connectors.map((connector) => {
                const updatedCharger = message.find(
                  (chargerFromMessage) =>
                    chargerFromMessage.id ===
                    connector?.chargerwise_connector_id
                )

                if (updatedCharger) {
                  const key = `${charger.id}_${connector.position}`
                  keysToReset.push(key)
                }

                return updatedCharger
                  ? { ...connector, status: updatedCharger.status }
                  : connector
              })

              return { ...charger, connectors: updatedConnectors }
            } else {
              const positionMap = {
                1: "left",
                2: "right",
              }

              const targetPosition = positionMap[message?.connector_id]

              if (charger.id === message?.charger_id) {
                const updatedConnectors = charger.connectors.map(
                  (connector) => {
                    if (
                      charger.connectors.length === 1 ||
                      connector.position?.toLowerCase().trim() ===
                        targetPosition?.toLowerCase() ||
                      connector.position?.toLowerCase().trim() === "single"
                    ) {
                      const key = `${charger.id}_${connector.position}`
                      keysToReset.push(key)

                      return {
                        ...connector,
                        status: message?.connector_status,
                      }
                    }

                    return connector
                  }
                )

                return { ...charger, connectors: updatedConnectors }
              }
            }

            return charger
          })

          return newData
        })

        setTimeout(() => {
          keysToReset.forEach((key) => {
            setConnectorLoading(key.split("_")[0], key.split("_")[1], false)
          })
        }, 0)
      })

      socket.on("stop-availability", (message) => {
        if (message?.charger_id) {
          setChargersWithStopPermission((prev) => ({
            ...prev,
            [message.charger_id]: true,
          }))
        }
      })
    })

    return () => {
      socket.off("status-update")
      socket.off("stop-availability")
    }
  }, [])

  useEffect(() => {
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, pagination.current_page, filter])

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: admin.deleteCharger + id,
      })

      if (response?.data?.code === 200) {
        toast.success(response.data.message)
        setData((data) => data.filter((user) => user.id !== id))

        setRecordsCount((prevCounts) => ({
          ...prevCounts,
          total_records: prevCounts.total_records - 1,
        }))

        setLoading(false)
      } else {
        setLoading(false)
        throw { message: response.data.message }
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const filterOnClick = () => {
    setOpen(!open)
  }

  return (
    <>
      {canAdd ? (
        <PageTitleButton
          PagetitleText={"Charger Management"}
          HeaderButtonText={"Add Charger"}
          IconclassName={"plus_black"}
          onClick={handleShow}
          filterShow={true}
          filterClick={filterOnClick}
          open={open}
        />
      ) : (
        <PageTitleButton
          PagetitleText={"Charging Stations"}
          onClick={handleShow}
          filterShow={true}
          filterClick={filterOnClick}
        />
      )}

      <div className="details_cards_wrapper mb-3">
        <div
          className={`bg_sea_green details_card ${
            filter === false ? "card_active" : ""
          }`}
          onClick={() => {
            setLoading(true)
            setPagination({ current_page: 1 })
            setFilter(false)
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Total Chargers</h3>
            <span className="card_icon">
              <i className="icon_main chargin_stations"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount?.total_records || 0}</p>
        </div>
        <div
          className={`bg_light_green details_card ${
            filter === "Available" ? "card_active" : ""
          }`}
          onClick={() => {
            setLoading(true)
            setPagination({ current_page: 1 })
            setFilter("Available")
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Active Chargers</h3>
            <span className="card_icon">
              <i className="icon_main activechecked"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount?.active_records || 0}</p>
        </div>
        <div
          className={`bg_light_red details_card ${
            filter === "Unavailable" ? "card_active" : ""
          }`}
          onClick={() => {
            setLoading(true)
            setPagination({ current_page: 1 })
            setFilter("Unavailable")
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Inactive Chargers</h3>
            <span className="card_icon">
              <i className="icon_main archive_data"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount?.inactive_records || 0}</p>
        </div>
      </div>

      <Collapse in={open}>
        <div id="brand-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <SearchBox
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e?.target?.value)
                    setPagination({ current_page: 1 })
                    setLoading(true)
                  }}
                />
              </Stack>
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 10}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<ChargerListSkeleton infiniteScroll={true} rows={1} />}
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th className="text-center">SN Number</th>
                    <th>Charger Details</th>
                    <th className="text-center">Connector</th>
                    <th className="text-center">Output Voltage</th>
                    <th className="text-center">Output Current</th>
                    <th className="text-center">Status</th>
                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 &&
                    data?.map((item, index) => (
                      <tr key={index + 1}>
                        <td className="text-center">{item?.id || "-"}</td>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-1 fs_14">
                            <span className="text-body-secondary">
                              Model Name:
                            </span>
                            {item?.model_name}
                          </div>
                          <div className="d-flex align-items-center gap-1 fs_14">
                            <span className="text-body-secondary">
                              Firmware Version:
                            </span>
                            {item?.firmware_version}
                          </div>
                          <div className="d-flex align-items-center gap-1 fs_14">
                            <span className="text-body-secondary">
                              Rated Power:
                            </span>
                            {item?.power_rating}
                          </div>
                          <div className="d-flex align-items-center gap-1 fs_14">
                            <span className="text-body-secondary">Vendor:</span>
                            {item?.vendor}
                          </div>
                        </td>
                        <td>
                          <div className="sppted_connectors">
                            {item?.connectors?.map((connector, index) => (
                              <div
                                className="connector_item position-relative"
                                key={index + 1}
                              >
                                <span
                                  className="bg-body border border-opacity-75 d-inline-flex justify-content-center align-items-center fs_14 p-1 rounded-circle text-body text-uppercase position-absolute end-0"
                                  style={{ width: "22px", height: "22px" }}
                                >
                                  {connector?.position?.charAt(0).toUpperCase()}
                                </span>
                                <img
                                  src={
                                    connector?.connector_img
                                      ? process.env.REACT_APP_RESOURCE_URL +
                                        connector?.connector_img
                                      : "https://placehold.co/50x50.png"
                                  }
                                  alt="connector_img"
                                  className="connector_item_img"
                                />
                                <span className="connector_nm d-flex mb-1">
                                  {connector?.connector_type}
                                </span>
                                <span
                                  className={`badges ${getStatusClassName(
                                    connector?.status
                                  )} fs_12 fw-normal py-1 px-2 d-flex`}
                                >
                                  {connector?.status}
                                </span>
                                {wsLoadingMap[
                                  `${item?.id}_${connector?.position}`
                                ] ? (
                                  <span
                                    className="mt-1 rounded-2 border border-dark-subtle opacity-75 text-dark-emphasis fs_12 fw-normal py-1 px-2 d-flex"
                                    style={{ cursor: "not-allowed" }}
                                  >
                                    <Spinner
                                      size="sm"
                                      animation="border"
                                      variant="secondary"
                                    />
                                  </span>
                                ) : connector?.status === "Preparing" ? (
                                  <span
                                    className="mt-1 rounded-2 border border-danger text-danger fs_12 fw-normal py-1 px-2 d-flex"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      transactionControl(
                                        item?.id,
                                        connector?.position
                                      )
                                      setConnectorLoading(
                                        item?.id,
                                        connector?.position,
                                        true
                                      )
                                    }}
                                  >
                                    Start
                                  </span>
                                ) : connector?.status === "Charging" &&
                                  chargersWithStopPermission[item?.id] ? (
                                  <span
                                    className="mt-1 rounded-2 border border-danger text-danger fs_12 fw-normal py-1 px-2 d-flex"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      transactionControl(
                                        item?.id,
                                        connector?.position,
                                        true
                                      )
                                      setConnectorLoading(
                                        item?.id,
                                        connector?.position,
                                        true
                                      )
                                    }}
                                  >
                                    Stop
                                  </span>
                                ) : (
                                  <span
                                    className="mt-1 rounded-2 border border-dark-subtle opacity-75 text-dark-emphasis fs_12 fw-normal py-1 px-2 d-flex"
                                    style={{ cursor: "not-allowed" }}
                                  >
                                    Start
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="text-center">{item?.output_voltage}</td>
                        <td className="text-center">{item?.output_current}</td>
                        <td className="text-center">
                          <Form.Check
                            type="switch"
                            id="custom-switch"
                            checked={item?.status === "Available"}
                            onChange={() => {
                              handleShowAlert()
                              setIsUpdate(item)
                            }}
                          />
                        </td>
                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              {canEdit && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText={"Edit"}
                                >
                                  <Button
                                    variant="btnedit_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      handleShow()
                                      setIsUpdate(item)
                                    }}
                                  >
                                    <i className="icon_main edit_pencil"></i>
                                  </Button>
                                </TooltipComponent>
                              )}
                              {canDelete && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText={"Delete"}
                                >
                                  <Button
                                    variant="btndelete_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      setShowAlert(true)
                                      setIsUpdate(item)
                                      setIsDelete(true)
                                    }}
                                  >
                                    <i className="icon_main delete_trash"></i>
                                  </Button>
                                </TooltipComponent>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  {data && !data?.length && (
                    <tr>
                      <td className="text-center py-4" colSpan={9}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <ChargerListSkeleton />
      )}

      <AddEditCharger
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        fetchData={fetchData}
        setIsUpdate={setIsDelete}
        setData={setData}
        isUpdate={isUpdate}
      />

      <SweetAlerts
        showAlert={showAlert}
        loading={loading}
        alertMessage={
          isDelete
            ? "Are you sure you want to Delete this Charger?"
            : `Are you sure you want to ${
                isUpdate.status === "Available" ? "Unavailable" : "Available"
              } this Charger?`
        }
        handleClose={handleClose}
        iconClassName={isDelete ? "delete_trash" : "exclamation_circle"}
        handleAlert={() => {
          isDelete
            ? handleDelete(isUpdate?.id)
            : handleStatusChange(isUpdate?.id, isUpdate?.status)
        }}
      />
    </>
  )
}

export default ChargerManagement
